import React, { useId } from 'react';
type VolumeCoverProps = {
  number: number;
  /** Title split into the lines it prints on (max two). */
  lines: string[];
  className?: string;
  style?: React.CSSProperties;
};
const CREAM = '#F5F0DC';
const RED = '#C8323A';
const BLUE = '#2C5FB8';
const CONE = '#F07F2A';
type Pt = [number, number];

/** Station positions on the cover: the Station Triangle seen from above. */
const STATIONS: Pt[] = [[45, 37], [21, 72], [69, 72]];
const ring = (c: Pt, r: number, deg: number): Pt => [c[0] + r * Math.sin(deg * Math.PI / 180), c[1] - r * Math.cos(deg * Math.PI / 180)];

/** Curved rotation arrow around a station, from a0 to a1 degrees. */
function arrowPath(c: Pt, r: number, a0: number, a1: number) {
  const [x0, y0] = ring(c, r, a0);
  const [x1, y1] = ring(c, r, a1);
  return `M${x0.toFixed(2)} ${y0.toFixed(2)} A${r} ${r} 0 0 1 ${x1.toFixed(2)} ${y1.toFixed(2)}`;
}

/**
 * Code-drawn cover in the same look as the real Volume 1 cover: grass seen from
 * above, the three stations with players and cones, the cream title, the gold
 * badges and the cream band.
 */
export function VolumeCover({
  number,
  lines,
  className = '',
  style
}: VolumeCoverProps) {
  const uid = useId().replace(/:/g, '');
  const label = `No-Queue Cricket, Volume ${number}: ${lines.join(' ')}`;
  const titleLines = [`Volume ${number}:`, ...lines];
  const lastY = 131;
  const lineH = 9.6;
  return <div role="img" aria-label={label} style={style} className={`cover-grass relative aspect-[9/16] overflow-hidden rounded-[5px] shadow-book ring-1 ring-black/40 ${className}`} data-id="element-426" data-mp="37fzkz8heqp5t">
      <svg viewBox="0 0 90 160" className="absolute inset-0 h-full w-full" aria-hidden="true" data-id="element-427" data-mp="2yg2poo0iis1s">
        <defs data-id="element-428" data-mp="2tgzya6dggkca">
          <radialGradient id={`r${uid}`} cx="35%" cy="30%" r="75%" data-id="element-429" data-mp="2th9ljm748dea">
            <stop offset="0" stopColor="#F07A80" data-id="element-430" data-mp="wo7s1cb3co2k" />
            <stop offset=".55" stopColor={RED} data-id="element-431" data-mp="wllodnrbmspm" />
            <stop offset="1" stopColor="#7E161B" data-id="element-432" data-mp="robqdjpv98q6" />
          </radialGradient>
          <radialGradient id={`b${uid}`} cx="35%" cy="30%" r="75%" data-id="element-433" data-mp="1at83v5xer2qx">
            <stop offset="0" stopColor="#7FA6F0" data-id="element-434" data-mp="1arwzp501vsza" />
            <stop offset=".55" stopColor={BLUE} data-id="element-435" data-mp="1fk6p8jx96r11" />
            <stop offset="1" stopColor="#173A7E" data-id="element-436" data-mp="1fm1ajzcfsl5n" />
          </radialGradient>
          <linearGradient id={`v${uid}`} x1="0" y1="0" x2="0" y2="1" data-id="element-437" data-mp="11u38u9l21rji">
            <stop offset="0" stopColor="#000" stopOpacity=".18" data-id="element-438" data-mp="16qtoivsy6l4e" />
            <stop offset=".35" stopColor="#000" stopOpacity="0" data-id="element-439" data-mp="16p8wbcu3ve9p" />
            <stop offset=".7" stopColor="#000" stopOpacity=".05" data-id="element-440" data-mp="1uhpahcaq2m3q" />
            <stop offset="1" stopColor="#000" stopOpacity=".45" data-id="element-441" data-mp="1yj2jtwd0x7fe" />
          </linearGradient>
          <marker id={`m${uid}`} viewBox="0 0 6 6" refX="3" refY="3" markerWidth="2.6" markerHeight="2.6" orient="auto-start-reverse" data-id="element-442" data-mp="1kjjbp3k7pw33">
            <path d="M0 0 L6 3 L0 6 Z" fill={CREAM} data-id="element-443" data-mp="1pgjijgovz2v9" />
          </marker>
        </defs>

        {/* boundary arc and side crease marks */}
        <circle cx="45" cy="88" r="70" fill="none" stroke={CREAM} strokeOpacity=".38" strokeWidth=".55" data-id="element-444" data-mp="e2xsltb7cx8o" />
        <path d="M2 44 H12 M7 39 V49 M78 44 H88 M83 39 V49" stroke={CREAM} strokeOpacity=".35" strokeWidth=".5" data-id="element-445" data-mp="6j6esg5vre0m" />

        {/* the Station Triangle */}
        <path d={`M${STATIONS[0][0]} ${STATIONS[0][1] + 6} L${STATIONS[1][0] + 5} ${STATIONS[1][1] - 4} M${STATIONS[0][0]} ${STATIONS[0][1] + 6} L${STATIONS[2][0] - 5} ${STATIONS[2][1] - 4} M${STATIONS[1][0] + 8} ${STATIONS[1][1]} L${STATIONS[2][0] - 8} ${STATIONS[2][1]}`} stroke={CREAM} strokeOpacity=".55" strokeWidth=".55" strokeDasharray="1.6 1.4" fill="none" data-id="element-446" data-mp="1jtspxmu2az3" />

        {/* station 1: the net */}
        <g data-id="element-447" data-mp="3sqwj4muukd89">
          <rect x="40.5" y="32.5" width="9" height="8" fill="#0E2208" fillOpacity=".5" stroke={CREAM} strokeOpacity=".85" strokeWidth=".45" data-id="element-448" data-mp="3srodjucpk4wy" />
          <path d="M43.5 32.5 V40.5 M46.5 32.5 V40.5 M40.5 35.2 H49.5 M40.5 37.9 H49.5" stroke={CREAM} strokeOpacity=".35" strokeWidth=".25" data-id="element-449" data-mp="3ny3gc4ixveyt" />
        </g>
        {/* station 2: a batting strip with stumps */}
        <g data-id="element-450" data-mp="38xzm4yp8c5am">
          <rect x="18.5" y="64" width="5" height="13" fill="#A9C98A" fillOpacity=".35" stroke={CREAM} strokeOpacity=".8" strokeWidth=".4" data-id="element-451" data-mp="38y7zsst6femv" />
          <path d="M20 65.2 V67.4 M21 65.2 V67.4 M22 65.2 V67.4" stroke={CREAM} strokeWidth=".35" data-id="element-452" data-mp="3dxudf3cx2ww7" />
        </g>
        {/* station 3: the bowling machine */}
        <g data-id="element-453" data-mp="3nmm1h0euwx1u">
          <rect x="66.5" y="67" width="5" height="7" rx=".8" fill="#0B1608" stroke="#5E6B57" strokeWidth=".4" data-id="element-454" data-mp="3nn44cfw0y9iq" />
          <circle cx="69" cy="68.6" r="1.1" fill="#25331F" stroke="#7D8A75" strokeWidth=".25" data-id="element-455" data-mp="3rnfo7qgkc5t5" />
        </g>

        {STATIONS.map((c, si) => <g key={si} data-id="element-456" data-mp="k913jetlf5o" data-mpi={si}>
            {/* rotation arrows */}
            <path d={arrowPath(c, 12.5, -62, -28)} stroke={CREAM} strokeWidth=".55" fill="none" markerEnd={`url(#m${uid})`} data-id="element-457" data-mp="5ikeg44bq7sn" />
            <path d={arrowPath(c, 12.5, 28, 62)} stroke={CREAM} strokeWidth=".55" fill="none" markerEnd={`url(#m${uid})`} data-id="element-458" data-mp="afkiyaliz2hn" />
            <path d={arrowPath(c, 12.5, 118, 152)} stroke={CREAM} strokeWidth=".55" fill="none" markerEnd={`url(#m${uid})`} data-id="element-459" data-mp="2c7p9bhndlrq9" />
            {/* players */}
            {[-90, -10, 75, 180, 255].map((deg, i) => {
          const [x, y] = ring(c, 10, deg);
          return <g key={deg} data-id="element-460" data-mp="ojrm2xpbohl8" data-mpi={i}>
                  <ellipse cx={x + 0.4} cy={y + 0.9} rx="2.3" ry="1.3" fill="#000" fillOpacity=".35" data-id="element-461" data-mp="kk0wgxbj87ia" />
                  <circle cx={x} cy={y} r="2.15" fill={`url(#${i % 2 === 0 ? 'b' : 'r'}${uid})`} data-id="element-462" data-mp="flpoyt8pm938" />
                </g>;
        })}
            {/* cones */}
            {[140, 220].map(deg => {
          const [x, y] = ring(c, 6.5, deg);
          return <path key={deg} d={`M${x} ${y - 1.5} L${x + 1.25} ${y + 0.9} L${x - 1.25} ${y + 0.9} Z`} fill={CONE} data-id="element-463" data-mp="1880os1ohs5yz" />;
        })}
          </g>)}

        <rect x="0" y="0" width="90" height="160" fill={`url(#v${uid})`} data-id="element-464" data-mp="yamao8q1jt9u" />

        {/* brand */}
        <text x="45" y="8.6" textAnchor="middle" fill={CREAM} fontFamily="'Inter Tight', Inter, sans-serif" fontWeight="700" fontSize="3.6" data-id="element-465" data-mp="3o20xfsio7cme">
          No-Queue Cricket
        </text>

        {/* title */}
        {titleLines.map((line, i) => <text key={line} x="45" y={lastY - (titleLines.length - 1 - i) * lineH} textAnchor="middle" fill={CREAM} fontFamily="'Inter Tight', Inter, sans-serif" fontWeight="800" fontSize="8.6" letterSpacing="-0.2" data-id="element-466" data-mp="3kh8yosssn6ji" data-mpi={i}>
            {line}
          </text>)}

        {/* badges */}
        {[{
        x: 6.5,
        w: 16.5,
        t: '15 Sessions'
      }, {
        x: 24.5,
        w: 17.5,
        t: '60/75/90 min'
      }, {
        x: 43.5,
        w: 19.5,
        t: 'Soft & Hard Ball'
      }, {
        x: 64.5,
        w: 19.5,
        t: 'Wet-Weather Swap'
      }].map(b => <g key={b.t} data-id="element-467" data-mp="2wuix1skcxdzf">
            <rect x={b.x} y="136.3" width={b.w} height="5" rx="1.1" fill="#E3B151" data-id="element-468" data-mp="2wtr1uiuo9i9o" />
            <text x={b.x + b.w / 2} y="139.6" textAnchor="middle" fill="#1C1403" fontFamily="Inter, sans-serif" fontWeight="600" fontSize="2.05" data-id="element-469" data-mp="2iv13z1013ek9">
              {b.t}
            </text>
          </g>)}

        {/* band */}
        <rect x="0" y="145.2" width="90" height="5.6" fill={CREAM} data-id="element-470" data-mp="2493tfr4vc0v6" />
        <text x="45" y="148.75" textAnchor="middle" fill="#0F1B3D" fontFamily="Inter, sans-serif" fontWeight="700" fontSize="1.75" letterSpacing=".12" data-id="element-471" data-mp="1z97wgbul99p4">
          SETUP &amp; ROLES  |  DRILLS &amp; ROTATIONS  |  GAME SENSE  |  ASSESS &amp; ADAPT
        </text>
      </svg>
    </div>;
}